import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { AlertCircle, ArrowLeft, Plus, Loader2, Pencil, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import type { GrRewardTypeLimitResponse } from '@/lib/api';
import { useRewardTypeLimits } from '../hooks';

export function AdminLimitsPage() {
  const { limits, loading, error, refresh } = useRewardTypeLimits();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLimit, setEditingLimit] = useState<GrRewardTypeLimitResponse | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Form state
  const [rewardType, setRewardType] = useState('');
  const [minSeconds, setMinSeconds] = useState('60');
  const [maxPerDay, setMaxPerDay] = useState('100');

  const resetForm = () => {
    setEditingLimit(null);
    setRewardType('');
    setMinSeconds('60');
    setMaxPerDay('100');
    setFormError(null);
  };

  const openEdit = (limit: GrRewardTypeLimitResponse) => {
    setEditingLimit(limit);
    setRewardType(limit.rewardType);
    setMinSeconds(String(limit.minSecondsBetweenRewards));
    setMaxPerDay(String(limit.maxRewardsPerDay));
    setFormError(null);
    setDialogOpen(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rewardType.trim()) return;

    setSaving(true);
    setFormError(null);
    try {
      if (editingLimit) {
        await api.updateGameRewardsLimit(editingLimit.limitId, {
          minSecondsBetweenRewards: Number(minSeconds),
          maxRewardsPerDay: Number(maxPerDay),
        });
      } else {
        await api.createGameRewardsLimit({
          rewardType: rewardType.trim(),
          minSecondsBetweenRewards: Number(minSeconds),
          maxRewardsPerDay: Number(maxPerDay),
        });
      }
      setDialogOpen(false);
      resetForm();
      refresh();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save limit');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (limitId: string) => {
    try {
      await api.deleteGameRewardsLimit(limitId);
      refresh();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to delete limit');
    }
  };

  const handleDialogChange = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      resetForm();
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div>
        <Link
          to="/a/arche_game_rewards/admin"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Admin
        </Link>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Rate Limits</h1>
          <Dialog open={dialogOpen} onOpenChange={handleDialogChange}>
            <DialogTrigger asChild>
              <Button size="sm" onClick={() => resetForm()}>
                <Plus className="h-4 w-4 mr-2" />
                Add Limit
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{editingLimit ? 'Edit Rate Limit' : 'Create Rate Limit'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSave} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="limitRewardType">Reward Type *</Label>
                  <Input
                    id="limitRewardType"
                    value={rewardType}
                    onChange={(e) => setRewardType(e.target.value)}
                    placeholder="currency"
                    disabled={!!editingLimit}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="limitMinSeconds">Min Seconds Between Rewards</Label>
                  <Input
                    id="limitMinSeconds"
                    type="number"
                    value={minSeconds}
                    onChange={(e) => setMinSeconds(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Minimum time between consecutive rewards of this type for a user.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="limitMaxPerDay">Max Rewards Per Day</Label>
                  <Input
                    id="limitMaxPerDay"
                    type="number"
                    value={maxPerDay}
                    onChange={(e) => setMaxPerDay(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Maximum number of rewards of this type a user can receive per day.
                  </p>
                </div>

                {formError && <p className="text-sm text-destructive">{formError}</p>}

                <Button type="submit" className="w-full" disabled={saving}>
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {editingLimit ? 'Update Limit' : 'Create Limit'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {(error || formError) && (
        <div className="flex items-center gap-2 rounded-md border border-destructive bg-destructive/10 p-3">
          <AlertCircle className="h-4 w-4 text-destructive" />
          <p className="text-sm text-destructive">{error || formError}</p>
        </div>
      )}

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-12 rounded-lg" />
          ))}
        </div>
      ) : limits.length === 0 ? (
        <p className="text-center text-muted-foreground py-12">
          No rate limits configured. Click "Add Limit" to create one.
        </p>
      ) : (
        <Card>
          <CardContent className="pt-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reward Type</TableHead>
                  <TableHead>Min Seconds Between</TableHead>
                  <TableHead>Max Per Day</TableHead>
                  <TableHead className="w-20" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {limits.map((limit) => (
                  <TableRow key={limit.limitId}>
                    <TableCell className="font-medium">{limit.rewardType}</TableCell>
                    <TableCell>{limit.minSecondsBetweenRewards}s</TableCell>
                    <TableCell>{limit.maxRewardsPerDay}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => openEdit(limit)}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Rate Limit</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete this rate limit? This action cannot
                                be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(limit.limitId)}>
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
